import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// Dispatcher.

import {
  getUserOpinions,
  getUserOpinionsNextPage,
} from 'dispatcher/opinionDispatcher';
import { getUserReviews } from 'dispatcher/reviewDispatcher';
import { imageUrl } from 'theme';

// Redux.
import {
  getProfileToDisplay,
  getProfileLatestCredentials,
  getDefaultProfileFilter,
} from 'reducer/profileRedux';
import useIsMobile from 'utils/hooks/useIsMobile';

// Constants.
import { PROFILE_FILTER_TYPE } from 'constants/profile';
import { REVIEW_PAGE_TYPE } from 'constants/product';

// Components.
import PostList from 'components/post/PostList';
import CommentListWithEntity from 'components/comment/CommentListWithEntity';
import ReviewList from 'components/review/ReviewList';
import OpinionList from 'components/opinion/OpinionList';
import { getUserPostsRequest } from 'entity/post/postRedux';
import {
  getUserParentCommentsRequest,
  getUserChildCommentsRequest,
} from 'entity/comment/commentRedux';
import { useUserComments } from 'entity/comment/commentHooks';
import { RootState } from 'reducer';
import CreditHistoryModal from 'components/reward/CreditHistoryModal';
import { isNewToSeedly } from 'entity/user/userUtils';
import ProfileTopContributeCard from './ProfileTopContributeCard';
import ProfileEnrollCard from './ProfileEnrollCard';
import ProfileCredential from './ProfileCredential';
import ProfileAvatar from './ProfileAvatar';
import ProfileDetail from './ProfileDetail';
import ProfileFilterBar from './ProfileFilterBar';
import EditProfileButton from './EditProfileButton';

const sort = {
  by: 'created_at',
  dir: 'desc',
};

const CustomProfileEmpty = ({ postType, name }) => {
  const emptyText = `${name} hasn’t published any ${postType} yet.
              When they do, their content will show up here.`;

  return (
    <div className="center-in-div p-4 rounded bg-white h-[130px] w-full">
      <p className="whitespace-pre-line text-center text-neutral-500">
        {emptyText}
      </p>
    </div>
  );
};

const Profile = props => {
  const { handleEditProfileOptions } = props;

  // Selectors
  const profile = useSelector(getProfileToDisplay);
  const initialProfileFilter = useSelector(getDefaultProfileFilter);
  const latestCredential = useSelector(getProfileLatestCredentials);
  const opinionPagination = useSelector(
    (state: RootState) => state.opinion.pagination,
  );
  const postPagination = useSelector(
    (state: RootState) => state.post.pagination,
  );
  const { pagination: commentPagination } = useUserComments();
  const isMobile = useIsMobile();

  // State
  const [profileFilter, setProfileFilter] = useState(null);

  const dispatch = useDispatch();

  // Refs
  const profileFilterBarRef = useRef(null);
  const scrollToComponent = useRef(null);

  const {
    description,
    name,
    image,
    links,
    slug,
    isOwnProfile,
    officialAccount,
  } = profile;

  let badgeUrl;
  let badgeDescription;
  if (isNewToSeedly(profile)) {
    badgeUrl = imageUrl.plant;
    badgeDescription = 'New to Seedly';
  }

  if (profile.officialAccount) {
    badgeUrl = imageUrl.claimed;
    badgeDescription = 'Official Seedly Account';
  }

  // Effects
  useEffect(() => {
    scrollToComponent.current = require('react-scroll-to-component');
  }, []);

  useEffect(() => {
    setProfileFilter(initialProfileFilter);
  }, [slug]);

  useEffect(() => {
    switch (profileFilter) {
      case PROFILE_FILTER_TYPE.OPINIONS:
        dispatch(getUserOpinions({ slug }));
        break;
      case PROFILE_FILTER_TYPE.ASKED_BY:
        dispatch(
          getUserPostsRequest({
            slug,
            page: 1,
            sort,
          }),
        );
        break;
      case PROFILE_FILTER_TYPE.ANSWERED_BY:
        dispatch(
          getUserParentCommentsRequest({
            slug,
            page: 1,
            per: 20,
          }),
        );
        break;
      case PROFILE_FILTER_TYPE.REPLIED_BY:
        dispatch(
          getUserChildCommentsRequest({
            slug,
            page: 1,
            per: 20,
          }),
        );
        break;
      case PROFILE_FILTER_TYPE.REVIEWS:
        dispatch(getUserReviews({ profileSlug: slug }));
        break;
      default:
        break;
    }
  }, [profileFilter, slug, dispatch]);

  const handleReviewPageChange = () => {
    scrollToComponent.current(profileFilterBarRef.current, {
      offset: -60,
      align: 'top',
      duration: 500,
    });
  };

  const renderList = () => {
    switch (profileFilter) {
      case PROFILE_FILTER_TYPE.REVIEWS:
        return (
          <ReviewList
            type={REVIEW_PAGE_TYPE.PROFILE}
            profileSlug={profile.slug}
            onPageChange={handleReviewPageChange}
            customEmpty={
              <CustomProfileEmpty postType="Reviews" name={profile.name} />
            }
          />
        );
      case PROFILE_FILTER_TYPE.OPINIONS:
        return (
          <OpinionList
            handleLoadMoreOpinion={() => {
              dispatch(
                getUserOpinionsNextPage({
                  pagination: opinionPagination,
                  slug,
                }),
              );
            }}
            customEmpty={
              <CustomProfileEmpty postType="Opinions" name={profile.name} />
            }
          />
        );
      case PROFILE_FILTER_TYPE.ASKED_BY:
        return (
          <PostList
            handleLoadMorePost={() => {
              dispatch(
                getUserPostsRequest({
                  page: postPagination?.currentPage + 1,
                  slug: profile.slug,
                  sort,
                }),
              );
            }}
            isOwnProfile={profile.isOwnProfile}
            customEmpty={
              <CustomProfileEmpty postType="posts" name={profile.name} />
            }
          />
        );
      case PROFILE_FILTER_TYPE.ANSWERED_BY:
        return (
          <CommentListWithEntity
            handleLoadMoreComment={() => {
              dispatch(
                getUserParentCommentsRequest({
                  page: commentPagination?.currentPage + 1,
                  slug: profile.slug,
                  per: 20,
                }),
              );
            }}
            customEmpty={
              <CustomProfileEmpty postType="comments" name={profile.name} />
            }
          />
        );
      case PROFILE_FILTER_TYPE.REPLIED_BY:
        return (
          <CommentListWithEntity
            handleLoadMoreComment={() => {
              dispatch(
                getUserChildCommentsRequest({
                  page: commentPagination?.currentPage + 1,
                  slug: profile.slug,
                  per: 20,
                }),
              );
            }}
            customEmpty={
              <CustomProfileEmpty postType="replies" name={profile.name} />
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-row w-full lg:w-auto">
      {!isMobile && (
        <div className="lg:w-[384px]">
          <div className="flex flex-col gap-2 w-full">
            <ProfileAvatar
              description={description}
              name={name}
              image={image}
              badgeUrl={badgeUrl}
              badgeDescription={badgeDescription}
              editProfileButton={
                isMobile &&
                isOwnProfile && (
                  <EditProfileButton
                    handleClick={() => handleEditProfileOptions(true)}
                  />
                )
              }
            />
            <ProfileCredential
              name={name}
              links={links}
              description={description}
              latestCredential={latestCredential}
            />
            <ProfileTopContributeCard />
            {isOwnProfile && !officialAccount && <ProfileEnrollCard />}
          </div>
        </div>
      )}
      <div className="w-full lg:w-[590px] lg:ml-4">
        <div className="flex flex-col items-center w-full gap-2">
          {isMobile && (
            <ProfileAvatar
              description={description}
              name={name}
              image={image}
              badgeUrl={badgeUrl}
              badgeDescription={badgeDescription}
              editProfileButton={
                isOwnProfile && (
                  <EditProfileButton
                    handleClick={() => handleEditProfileOptions(true)}
                  />
                )
              }
              profileDetail={
                <ProfileDetail
                  profile={profile}
                  defaultCredential={latestCredential.default}
                />
              }
            />
          )}
          {!isMobile && (
            <ProfileDetail
              profile={profile}
              defaultCredential={latestCredential.default}
              editProfileButton={
                isOwnProfile && (
                  <EditProfileButton
                    handleClick={() => handleEditProfileOptions(false)}
                  />
                )
              }
            />
          )}
          {isMobile && (
            <ProfileCredential
              name={name}
              links={links}
              description={description}
              latestCredential={latestCredential}
            />
          )}
          {isMobile && <ProfileTopContributeCard />}
          {isMobile && isOwnProfile && (
            <div className="card">
              <ProfileEnrollCard />
            </div>
          )}
          <div ref={profileFilterBarRef} style={{ width: '100%' }}>
            <ProfileFilterBar
              profile={profile}
              onClick={filter => setProfileFilter(filter)}
              selectedFilter={profileFilter}
            />
          </div>
          {renderList()}
        </div>
        <CreditHistoryModal />
      </div>
    </div>
  );
};

export default Profile;
